import { type ChangeEvent, type FC, useEffect, useMemo, useState } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useGetEnrollmentRiskReturnData, useSubmitEnrollmentRiskReturnData } from '@egw/api'
import { Button, GenericError, RangeSliderV2, RangeSliderVariantV2, RangeGraphV2 } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentLayout } from '@egw/layouts'
import { useRouter } from '@egw/router'
import { CUSTOMER_SERVICE_PHONE_NUMBER, CUSTOMER_SERVICE_PHONE_NUMBER_DISPLAY, WEALTH_ROUTES } from '@egw/constants'
import { type InvestmentRiskToleranceCategory } from '@egw/api/src/models/enrollment/enrollment'
import { PageHeader } from '../../../components'
import { RiskReturnAssetAllocation } from '../../../components/risk-return-asset-allocation/risk-return-asset-allocation'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'

const log = new Logger('Enrollment - Risk and Return')

interface Allocations {
  cashEquivalentPercent: number
  equityPercent: number
  fixedIncomePercent: number
}

interface RiskTolerance {
  category: string
  index: number
  allocation: Allocations
}

export const RiskReturnPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()

  const { data, isLoading, error } = useGetEnrollmentRiskReturnData()
  const { mutate: submit, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentRiskReturnData()

  const riskTolerances = data?.enrollmentData.recommendation.investment.riskTolerance.riskTolerances
  const recommendedRiskTolerance = data?.enrollmentData.recommendation.investment.riskTolerance.recommendedRiskTolerance
  const allowedRiskTolerances = data?.enrollmentData.recommendation.investment.riskTolerance.allowedRiskTolerances

  const recommendedValue = useMemo(() => {
    return riskTolerances?.find((item) => item.category === recommendedRiskTolerance)
  }, [riskTolerances, recommendedRiskTolerance])

  const [currentRiskTolerance, setCurrentRiskTolerance] = useState<RiskTolerance | undefined>(recommendedValue)

  const onChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const newValue = Number(e.target.value)
    const newRiskTolerance = riskTolerances?.find((item) => item.index === newValue)
    setCurrentRiskTolerance(newRiskTolerance)
  }

  // Slider filled area
  const filledArea = useMemo(() => {
    return riskTolerances
      ?.reduce<number[]>((acc, item) => {
        if (allowedRiskTolerances?.includes(item.category)) {
          acc.push(item.index)
        }
        return acc
      }, [])
      .sort((a, b) => a - b)
  }, [riskTolerances, allowedRiskTolerances])

  // Returns true if currentRiskTolerance category is not in the recommended range
  const isNotInRecommendedRange = useMemo(() => {
    return !allowedRiskTolerances?.includes(currentRiskTolerance?.category as InvestmentRiskToleranceCategory)
  }, [allowedRiskTolerances, currentRiskTolerance])

  // Get Title
  const getTitle = useMemo(() => {
    const category = riskTolerances?.find((item) => item.index === currentRiskTolerance?.index)?.category
    return category
      ? category
          .split('_')[0]
          .toLowerCase()
          .replace(/^\w/, (c) => c.toUpperCase())
      : ''
  }, [currentRiskTolerance?.index, riskTolerances])

  const handleContinueClick = async (): Promise<void> => {
    try {
      const response = await submit({
        investmentProfile: {
          riskTolerance: currentRiskTolerance?.category as InvestmentRiskToleranceCategory,
        },
      })
      if (response?.path) {
        navigate(response.path)
      }
    } catch (err) {
      log.error('Error submitting enrollment intro data', err)
    }
  }

  useValidateRoute(data?.path)

  // Update Current RiskTolerances value
  useEffect(() => {
    if (recommendedValue) {
      setCurrentRiskTolerance(recommendedValue)
    }
  }, [recommendedValue])

  return (
    <EnrollmentLayout className="bg-background-light" title="Risk Return">
      <div className="flex flex-col w-full">
        <div className="egw-container-enroll !pb-0 h-full flex flex-col pb-0">
          {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
          {isLoading ? (
            <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
              <Skeleton count={1} height="100px" className="mb-10" />
              <Skeleton count={5} height="50px" />
            </SkeletonTheme>
          ) : null}

          {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
          {!isLoading ? (
            <>
              <PageHeader title={t('investments.risk-return.title')} />
              <p className="typo-primary-sm-semibold text-text-meta mb-sm mt-sm">
                {t('investments.risk-return.asset_allocation')}
              </p>

              <RiskReturnAssetAllocation currentRiskTolerance={currentRiskTolerance?.allocation} />
              <p className="typo-primary-sm-semibold text-text-meta -mb-md mt-xl">
                {t('investments.risk-return.expected_return')}
              </p>
              <RangeGraphV2 value={currentRiskTolerance?.index ?? 0} style={{ width: '99%', height: '260px' }} />
            </>
          ) : null}

          {/* This error handles the case when the initial data is not loaded, for example network error */}
          {error ? (
            <div className="flex-1 flex flex-col justify-start md:justify-center">
              <GenericError errorMessage={t('common.generic_error')} />
            </div>
          ) : null}
        </div>

        {!isLoading && !error && !submitError ? (
          <div className="bg-white bottom-0 left-0 right-0">
            <div
              className={`egw-container-enroll !pt-md flex flex-col ${isNotInRecommendedRange ? 'mb-0 !pb-sm' : ''}`}
            >
              <p className="typo-primary-md-semibold text-text-header mb-2xs">
                {t('investments.risk-return.risk_tolerance')}
              </p>
              <p className="typo-primary-sm-regular text-text-body mb-sm">
                {t('investments.risk-return.risk_tolerance_description')}
              </p>
              <p
                className={`typo-primary-xl-semibold capitalize mb-2 ${isNotInRecommendedRange ? '' : 'text-sky-400'}`}
              >
                {getTitle}
              </p>

              {currentRiskTolerance?.index !== undefined ? (
                <RangeSliderV2
                  variant={RangeSliderVariantV2.Split}
                  fillFrom={filledArea?.[0] ?? 0}
                  fillTo={filledArea?.[filledArea.length - 1] ?? 0}
                  recommendedLabel="Recommended"
                  onChange={onChange}
                  value={currentRiskTolerance.index}
                  recommendedValue={recommendedValue?.index}
                  isShowRecommendedLabel={!isNotInRecommendedRange}
                  testId="range-slider"
                />
              ) : null}

              {isNotInRecommendedRange ? (
                <div className="p-sm bg-background-light -mt-2xs z-[1]" data-testid="not-in-recommended-range">
                  <p className="typo-primary-sm-regular mt-sm">{t('investments.risk-return.outside_range')}</p>
                  <p className="typo-primary-sm-regular mt-xs">
                    {t('common.call')}&nbsp;
                    <a
                      href={`tel:${CUSTOMER_SERVICE_PHONE_NUMBER}`}
                      className="typo-primary-sm-semibold  text-text-action"
                    >
                      {CUSTOMER_SERVICE_PHONE_NUMBER_DISPLAY}
                    </a>
                  </p>
                </div>
              ) : null}
              <Button
                type="submit"
                className={`w-full md:w-min ${isNotInRecommendedRange ? 'mt-sm' : 'mt-3xl'}`}
                isDisabled={isNotInRecommendedRange}
                isLoading={isSubmitting}
                testId="enrollment-continue-button"
                onClick={() => {
                  void handleContinueClick()
                }}
              >
                {t('common.continue')}
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </EnrollmentLayout>
  )
}
